import React, { useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  LayersControl,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L, { LeafletMouseEvent, DragEndEvent } from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { useT } from "../i18n";

const DefaultIcon = L.icon({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const DEFAULT_CENTER: [number, number] = [34.825, 135.435];
const DEFAULT_ZOOM = 18;

function parsePosition(lat: string | number, lng: string | number): [number, number] | null {
  const la = parseFloat(String(lat));
  const ln = parseFloat(String(lng));
  if (Number.isFinite(la) && Number.isFinite(ln)) return [la, ln];
  return null;
}

interface ClickHandlerProps {
  onPick: (lat: number, lng: number) => void;
}

function ClickHandler({ onPick }: ClickHandlerProps): null {
  useMapEvents({
    click(e: LeafletMouseEvent) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

interface RecenterProps {
  position: [number, number] | null;
  skipKey: React.MutableRefObject<string | null>;
}

function Recenter({ position, skipKey }: RecenterProps): null {
  const map = useMap();
  const lastKey = useRef<string | null>(null);
  if (position) {
    const key = `${position[0].toFixed(6)},${position[1].toFixed(6)}`;
    if (key !== lastKey.current) {
      lastKey.current = key;
      if (key !== skipKey.current && map.getCenter().distanceTo(position) > 1) {
        map.setView(position, Math.max(map.getZoom(), DEFAULT_ZOOM));
      }
    }
  }
  return null;
}

function InvalidateSize(): null {
  const map = useMap();
  useEffect(() => {
    const fix = () => map.invalidateSize();
    fix();
    const timer = setTimeout(fix, 200);
    const ro = new ResizeObserver(fix);
    ro.observe(map.getContainer());
    return () => {
      clearTimeout(timer);
      ro.disconnect();
    };
  }, [map]);
  return null;
}

interface MapPickerProps {
  lat: string | number;
  lng: string | number;
  onChange: (lat: string, lng: string) => void;
}

export default function MapPicker({ lat, lng, onChange }: MapPickerProps) {
  const t = useT();
  const position = parsePosition(lat, lng);
  const skipKey = useRef<string | null>(null);

  const handlePick = (la: number, ln: number) => {
    const sLat = la.toFixed(6);
    const sLng = ln.toFixed(6);
    skipKey.current = `${sLat},${sLng}`;
    onChange(sLat, sLng);
  };

  return (
    <div className="space-y-1 relative z-0">
      <div className="h-72 rounded-md overflow-hidden border">
        <MapContainer
          center={position || DEFAULT_CENTER}
          zoom={position ? DEFAULT_ZOOM : 15}
          style={{ height: "100%", width: "100%" }}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name={t("field.mapSatellite")}>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={20}
                maxNativeZoom={19}
                attribution="Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name={t("field.mapStandard")}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
            </LayersControl.BaseLayer>
          </LayersControl>

          <InvalidateSize />
          <ClickHandler onPick={handlePick} />
          <Recenter position={position} skipKey={skipKey} />

          {position && (
            <Marker
              position={position}
              draggable
              eventHandlers={{
                dragend: (e: DragEndEvent) => {
                  const p = (e.target as L.Marker).getLatLng();
                  handlePick(p.lat, p.lng);
                },
              }}
            />
          )}
        </MapContainer>
      </div>
      <p className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
        <i className="fa-solid fa-hand-pointer" aria-hidden="true" />
        <span>{t("field.mapHint")}</span>
        {position && (
          <a
            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
            href={`https://www.google.com/maps/search/?api=1&query=${position[0]},${position[1]}`}
            target="_blank"
            rel="noreferrer"
          >
            <i className="fa-solid fa-map-location-dot" aria-hidden="true" />
            <span>{t("field.openInMaps")}</span>
          </a>
        )}
      </p>
    </div>
  );
}
